import { ComponentProps, Ref } from 'preact';
import { useState } from 'preact/hooks';
import { TextTagList } from './TextTagList';
import { Flex } from '../UNSAFE_Flex';
import { TextFieldInput } from '../UNSAFE_TextField';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';

type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;
type TextTagListProps = ComponentProps<typeof TextTagList>;

type PassThroughTextFieldInputProps = Pick<
  TextFieldInputProps,
  | 'aria-controls'
  | 'aria-describedby'
  | 'aria-expanded'
  | 'aria-invalid'
  | 'aria-label'
  | 'autoFocus'
  | 'hasEmptyLabel'
  | 'hasInsideLabel'
  | 'id'
  | 'onBlur'
  | 'onFocus'
  | 'onInput'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'isRequired'
  | 'placeholder'
  | 'textAlign'
  | 'variant'
> & {
  inputRef?: Ref<HTMLInputElement>;
};
type PassThroughTextTagListProps = Pick<
  TextTagListProps,
  'onExitNavigation' | 'onRemove' | 'removeIcon'
>;

type Props = PassThroughTextFieldInputProps &
  PassThroughTextTagListProps & {
    displayValue?: string;
    isAddToListShown?: boolean;
    isKeyboardNavigable?: boolean;
    isTextTagListShown?: boolean;
    isUserFiltering?: boolean;
    liveRegionText?: string;
    selectedValuesDescriptionId?: string;
    selectedValuesKeyboardNavDescriptionId?: string;
    textTagListData: TextTagListProps['data'];
    textTagListRef?: TextTagListProps['ref'];
    userInput?: string;
    virtualKeyboard?: 'auto' | 'email' | 'number' | 'search' | 'tel' | 'text' | 'url';
  };

export function SelectMultipleFieldInput({
  displayValue,
  isAddToListShown,
  isKeyboardNavigable,
  isTextTagListShown,
  isUserFiltering,
  liveRegionText,
  onExitNavigation,
  onRemove,
  removeIcon,
  selectedValuesDescriptionId,
  selectedValuesKeyboardNavDescriptionId,
  textTagListData,
  textTagListRef,
  userInput,
  virtualKeyboard,
  ...passThroughTextFieldInputProps
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const addToListAccText = isAddToListShown ? translations.select_addToListAvailable() : undefined;

  // Add the text tags navigation accessible text to the aria description for the input if there
  // are any selected values and the component is keyboard navigable.
  // Also add an aria live region if the status changes while the input already has focus because
  // at that time the aria description will not be read out again.  Remove the aria live region
  // when the input loses focus.
  const accTextTagsNav =
    isKeyboardNavigable && displayValue && displayValue.length > 0
      ? translations.selectMultiple_highlightSelectedTagsInstructionText()
      : undefined;
  const { isInputFocused } = useFormFieldContext();
  const [prevAccTextTagsNav, setPrevAccTextTagsNav] = useState(accTextTagsNav);
  const [isAccTextTagsNavLive, setIsAccTextTagsNavLive] = useState(false);
  isInputFocused &&
  isTextTagListShown &&
  !prevAccTextTagsNav &&
  accTextTagsNav &&
  !isAccTextTagsNavLive
    ? setIsAccTextTagsNavLive(true)
    : !isInputFocused && isAccTextTagsNavLive
    ? setIsAccTextTagsNavLive(false)
    : {};
  if (prevAccTextTagsNav !== accTextTagsNav) {
    setPrevAccTextTagsNav(accTextTagsNav);
  }

  return (
    <>
      {isTextTagListShown && (
        <TextTagList
          aria-label={translations.selectMultiple_selectedValues()}
          data={textTagListData}
          hasInsideLabel={passThroughTextFieldInputProps.hasInsideLabel}
          maxWidth="50%"
          onExitNavigation={onExitNavigation}
          onRemove={onRemove}
          ref={textTagListRef}
          removeIcon={removeIcon}
        />
      )}
      <Flex flex={1}>
        <TextFieldInput
          aria-autocomplete="list"
          autoComplete="off"
          // When focused, we show the tag list, which can be treated as having prefix
          hasPrefix={isTextTagListShown}
          role="combobox"
          hasEndContent={true}
          spellcheck={false}
          type={virtualKeyboard === 'auto' ? 'search' : virtualKeyboard}
          value={isUserFiltering || isTextTagListShown ? userInput : displayValue}
          {...passThroughTextFieldInputProps}
        />
      </Flex>
      {
        // Show description text when focused as we will not be
        // showing display text in the input.
        isTextTagListShown && (
          <HiddenAccessible
            id={selectedValuesDescriptionId}
            // We want the content to be hidden for screen readers too by default.
            // But, it will be overridden when we link the aria-describedby attribute.
            // Otherwise, the content here will be read twice.
            isHidden={true}>
            {displayValue}
          </HiddenAccessible>
        )
      }
      {
        // Add the text tags navigation accessible text to the aria description for the input
        // if there are any selected values and the component is keyboard navigable.
        // (Don't add it if we're adding a live region for the same text because then it
        // gets read out twice by the screen reader, for some reason.)
        isTextTagListShown && accTextTagsNav && !isAccTextTagsNavLive && (
          <HiddenAccessible
            id={selectedValuesKeyboardNavDescriptionId}
            // We want the content to be hidden for screen readers too by default.
            // But, it will be overridden when we link the aria-describedby attribute.
            // Otherwise, the content here will be read twice.
            isHidden={true}>
            {accTextTagsNav}
          </HiddenAccessible>
        )
      }
      <LiveRegion>{liveRegionText}</LiveRegion>
      {
        // Add an aria live region for the text tags navigation accessible text if the status
        // changes while the input already has focus because at that time the aria description
        // will not be read out again.
        // (Add the region itself whenever the text tags are shown so that it's already in the
        // DOM when we want to populate it with text.  If we dynamically add the region itself
        // at that time, the screen reader (JAWS) may not read it at all, for some reason.)
        isTextTagListShown && (
          <LiveRegion>{isAccTextTagsNavLive ? accTextTagsNav : undefined}</LiveRegion>
        )
      }
      {isAddToListShown ? <LiveRegion>{addToListAccText}</LiveRegion> : undefined}
    </>
  );
}
